import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import AccessDenied from "@/pages/admin/components/AccessDenied";

interface BookingRecord {
  id: string;
  customer_name: string;
  phone: string;
  email: string | null;
  service_name: string;
  booking_date: string;
  booking_time: string;
  status: string | null;
  notes: string | null;
  created_at: string;
}

const STATUS_LABELS: Record<string, string> = {
  pending: "Chờ xác nhận",
  confirmed: "Đã xác nhận",
  completed: "Hoàn thành",
  cancelled: "Đã hủy",
};

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-accent-100 text-accent-700",
  confirmed: "bg-primary-100 text-primary-700",
  completed: "bg-secondary-100 text-secondary-700",
  cancelled: "bg-foreground-100 text-foreground-600",
};

export default function AdminBookings() {
  const { user, session, isAdmin, loading: authLoading, profileLoading } = useAuth();
  const [bookings, setBookings] = useState<BookingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-bookings`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${session?.access_token ?? ""}`,
          "Content-Type": "application/json",
        },
      });
      const text = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      if (!res.ok || parsed?.code !== "OK") {
        setError(parsed?.meta?.message || parsed?.message || text || "Lỗi khi tải dữ liệu");
        return;
      }
      setBookings(parsed.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Lỗi kết nối");
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (isAdmin) {
      fetchBookings();
    }
  }, [fetchBookings, isAdmin]);

  const updateStatus = async (id: string, status: string) => {
    setUpdatingId(id);
    try {
      const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-bookings`;
      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${session?.access_token ?? ""}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id, status }),
      });
      const text = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      if (!res.ok || parsed?.code !== "OK") {
        setError(parsed?.meta?.message || parsed?.message || "Cập nhật thất bại");
        return;
      }
      setBookings((prev) =>
        prev.map((b) => (b.id === id ? { ...b, status } : b))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Lỗi kết nối");
    } finally {
      setUpdatingId(null);
    }
  };

  const filtered = bookings.filter((b) => {
    const matchesStatus = filterStatus ? b.status === filterStatus : true;
    const term = searchTerm.trim().toLowerCase();
    const matchesSearch = term
      ? b.customer_name.toLowerCase().includes(term) ||
        b.phone.includes(term) ||
        b.service_name.toLowerCase().includes(term)
      : true;
    return matchesStatus && matchesSearch;
  });

  const stats = {
    total: bookings.length,
    pending: bookings.filter((b) => b.status === "pending").length,
    confirmed: bookings.filter((b) => b.status === "confirmed").length,
    completed: bookings.filter((b) => b.status === "completed").length,
    cancelled: bookings.filter((b) => b.status === "cancelled").length,
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
            <i className="ri-lock-line text-2xl" />
          </div>
          <h1 className="font-display text-2xl font-bold text-foreground-950 mb-2">
            Yêu cầu đăng nhập
          </h1>
          <p className="text-sm text-foreground-600 font-body mb-6">
            Vui lòng đăng nhập để truy cập trang quản lý lịch hẹn.
          </p>
          <Link
            to="/login"
            className="inline-block px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium rounded-md transition-colors cursor-pointer"
          >
            Đăng nhập
          </Link>
        </div>
      </div>
    );
  }

  if (profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
      </div>
    );
  }

  if (!isAdmin) {
    return <AccessDenied />;
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* Simple admin header */}
      <header className="bg-white border-b border-background-200/70 sticky top-0 z-30">
        <div className="w-full px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
                <i className="ri-leaf-line text-white text-sm" />
              </div>
              <span className="font-display text-lg font-bold text-foreground-950">
                Xanh Wellness
              </span>
            </Link>
            <span className="hidden sm:inline text-foreground-300">|</span>
            <span className="hidden sm:inline text-sm font-label font-medium text-foreground-600">
              Quản lý lịch hẹn
            </span>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <Link
              to="/admin/services"
              className="px-3 py-1.5 text-sm font-label font-medium text-foreground-600 hover:text-foreground-900 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              Dịch vụ
            </Link>
            <span className="hidden sm:inline text-sm text-foreground-600 font-body">
              {user.email}
            </span>
            <button
              onClick={() => supabase.auth.signOut()}
              className="px-3 py-1.5 text-sm font-label font-medium text-foreground-600 hover:text-foreground-900 border border-background-300/60 rounded-md transition-colors cursor-pointer"
            >
              Đăng xuất
            </button>
          </div>
        </div>
      </header>

      <main className="w-full px-4 md:px-8 py-6 md:py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mb-6 md:mb-8">
          {[
            { label: "Tổng lịch hẹn", value: stats.total, color: "text-foreground-900" },
            { label: "Chờ xác nhận", value: stats.pending, color: "text-accent-600" },
            { label: "Đã xác nhận", value: stats.confirmed, color: "text-primary-600" },
            { label: "Hoàn thành", value: stats.completed, color: "text-secondary-600" },
            { label: "Đã hủy", value: stats.cancelled, color: "text-foreground-500" },
          ].map((s) => (
            <div
              key={s.label}
              className="bg-white border border-background-200/70 rounded-lg p-4 md:p-5"
            >
              <p className="text-xs font-label text-foreground-500 mb-1">{s.label}</p>
              <p className={`font-display text-2xl md:text-3xl font-bold ${s.color}`}>
                {s.value}
              </p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white border border-background-200/70 rounded-lg p-4 md:p-5 mb-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-label font-medium text-foreground-700">Lọc:</span>
              {["", "pending", "confirmed", "completed", "cancelled"].map((s) => (
                <button
                  key={s || "all"}
                  onClick={() => setFilterStatus(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-label font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    filterStatus === s
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {s ? STATUS_LABELS[s] : "Tất cả"}
                </button>
              ))}
            </div>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Tìm theo tên, SĐT, dịch vụ..."
                className="w-full sm:w-64 pl-9 pr-4 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm font-body focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-background-200/70 rounded-lg overflow-hidden">
          {loading ? (
            <div className="py-20 text-center">
              <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 mb-3 inline-block" />
              <p className="text-sm text-foreground-500 font-body">Đang tải lịch hẹn...</p>
            </div>
          ) : error ? (
            <div className="py-16 text-center px-4">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                <i className="ri-error-warning-line text-xl" />
              </div>
              <p className="text-sm text-accent-700 font-body mb-4">{error}</p>
              <button
                onClick={fetchBookings}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-medium rounded-md transition-colors cursor-pointer"
              >
                Thử lại
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-calendar-line text-2xl" />
              </div>
              <p className="text-sm text-foreground-500 font-body">
                {searchTerm || filterStatus
                  ? "Không tìm thấy lịch hẹn phù hợp"
                  : "Chưa có lịch hẹn nào"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-50/50">
                    <th className="px-4 py-3 text-xs font-label font-semibold text-foreground-600 whitespace-nowrap">
                      Ngày & Giờ
                    </th>
                    <th className="px-4 py-3 text-xs font-label font-semibold text-foreground-600 whitespace-nowrap">
                      Dịch vụ
                    </th>
                    <th className="px-4 py-3 text-xs font-label font-semibold text-foreground-600 whitespace-nowrap">
                      Khách hàng
                    </th>
                    <th className="px-4 py-3 text-xs font-label font-semibold text-foreground-600 whitespace-nowrap">
                      Liên hệ
                    </th>
                    <th className="px-4 py-3 text-xs font-label font-semibold text-foreground-600 whitespace-nowrap">
                      Trạng thái
                    </th>
                    <th className="px-4 py-3 text-xs font-label font-semibold text-foreground-600 whitespace-nowrap">
                      Ghi chú
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => (
                    <tr
                      key={b.id}
                      className="border-b border-background-100 hover:bg-background-50/60 transition-colors"
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <p className="text-sm font-medium text-foreground-900 font-body">
                          {new Date(b.booking_date).toLocaleDateString("vi-VN")}
                        </p>
                        <p className="text-xs text-foreground-500 font-body">{b.booking_time}</p>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <p className="text-sm text-foreground-800 font-body">{b.service_name}</p>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <p className="text-sm font-medium text-foreground-900 font-body">
                          {b.customer_name}
                        </p>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <p className="text-sm text-foreground-700 font-body">{b.phone}</p>
                        {b.email && (
                          <p className="text-xs text-foreground-500 font-body">{b.email}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="relative">
                          <select
                            value={b.status || "pending"}
                            onChange={(e) => updateStatus(b.id, e.target.value)}
                            disabled={updatingId === b.id}
                            className={`appearance-none px-3 py-1.5 pr-7 rounded-full text-xs font-label font-medium border cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-100 disabled:opacity-50 ${
                              STATUS_COLORS[b.status || "pending"]
                            } border-transparent`}
                          >
                            {Object.entries(STATUS_LABELS).map(([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ))}
                          </select>
                          {updatingId === b.id ? (
                            <i className="ri-loader-4-line animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-foreground-400 text-xs" />
                          ) : (
                            <i className="ri-arrow-down-s-line absolute right-1.5 top-1/2 -translate-y-1/2 text-foreground-400 text-xs pointer-events-none" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-foreground-600 font-body max-w-[200px] truncate">
                          {b.notes || "—"}
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-foreground-400 font-body mt-4 text-center">
          Cập nhật lần cuối: {new Date().toLocaleString("vi-VN")}
        </p>
      </main>
    </div>
  );
}